import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Pencil, Trash2, Plus, Cog } from 'lucide-react'
import type { MachineProfile } from '../../types'
import { defaultProfile } from '../../hooks/useMachineProfiles'
import MachineProfileEditor from '../mission-control/MachineProfileEditor'
import EmptyState from '../ui/EmptyState'

interface MachinesPanelProps {
  profiles: MachineProfile[]
  onAdd: (profile: MachineProfile) => void
  onUpdate: (id: string, updates: Partial<MachineProfile>) => void
  onRemove: (id: string) => void
}

type View = 'list' | 'edit'

export default function MachinesPanel({ profiles, onAdd, onUpdate, onRemove }: MachinesPanelProps) {
  const [view, setView] = useState<View>('list')
  const [editing, setEditing] = useState<MachineProfile | null>(null)

  const handleNewProfile = () => {
    setEditing(defaultProfile())
    setView('edit')
  }

  const handleEdit = (profile: MachineProfile) => {
    setEditing({ ...profile })
    setView('edit')
  }

  const handleSave = () => {
    if (!editing) return
    const exists = profiles.some(p => p.id === editing.id)
    if (exists) {
      onUpdate(editing.id, editing)
    } else {
      onAdd(editing)
    }
    setView('list')
    setEditing(null)
  }

  const handleCancel = () => {
    setView('list')
    setEditing(null)
  }

  return (
    <AnimatePresence mode="wait">
      {view === 'list' ? (
        <motion.div
          key="list"
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.15 }}
          className="space-y-3"
        >
          {profiles.length === 0 ? (
            <EmptyState
              icon={Cog}
              title="No machine profiles"
              description="Create your first machine profile to get started."
              action={{ label: 'New Profile', onClick: handleNewProfile }}
            />
          ) : (
            profiles.map(profile => (
              <div
                key={profile.id}
                className="flex items-center justify-between p-4 glass rounded-xl hover:border-cyan-500/25 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-sm font-semibold text-slate-200 truncate">
                      {profile.name}
                    </span>
                    <span className="font-mono text-[10px] text-cyan-500/70 bg-cyan-500/10 px-2 py-0.5 rounded shrink-0">
                      {profile.controller}
                    </span>
                    <span className="font-mono text-[10px] text-slate-400 bg-white/5 border border-cyan-500/15 px-2 py-0.5 rounded shrink-0 capitalize">
                      {profile.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 font-mono text-[10px] text-slate-600">
                    <span>{profile.travelX}x{profile.travelY}x{profile.travelZ} mm</span>
                    <span>{profile.maxRpm.toLocaleString()} rpm</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4 shrink-0">
                  <button
                    onClick={() => handleEdit(profile)}
                    className="p-1.5 text-slate-500 hover:text-cyan-400 transition-colors"
                    aria-label="Edit profile"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onRemove(profile.id)}
                    className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                    aria-label="Delete profile"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}

          {profiles.length > 0 && (
            <button
              onClick={handleNewProfile}
              className="
                w-full flex items-center justify-center gap-2
                font-mono text-xs text-cyan-400 border border-dashed border-cyan-500/30
                rounded-xl py-3 mt-2
                hover:border-cyan-400 hover:bg-cyan-500/5 transition-all duration-150
              "
            >
              <Plus className="w-3.5 h-3.5" />
              New Profile
            </button>
          )}
        </motion.div>
      ) : (
        <motion.div
          key="edit"
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 12 }}
          transition={{ duration: 0.15 }}
        >
          {editing && (
            <MachineProfileEditor
              value={editing}
              onChange={setEditing}
              onSave={handleSave}
              onCancel={handleCancel}
              existingNames={profiles.filter(p => p.id !== editing.id).map(p => p.name)}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
